import { useTranslation } from '../../i18n/LanguageContext'
import { useWordMatch, type GameMode, type Difficulty } from './useWordMatch'
import { useTimer } from '../useTimer'
import { s } from './wordMatchStyles'
import GamePlayHeader from '../GamePlayHeader'

/* ── Play Screen ─────────────────────────────────────────────── */
export function PlayScreen({ state, difficulty, mode, onSelectOption, onSetInputValue, onSubmitInput, onToggleHint, onBack }: {
  state:            ReturnType<typeof useWordMatch>['state']
  difficulty:       Difficulty
  mode:             GameMode
  onSelectOption:   (id: number) => void
  onSetInputValue:  (val: string) => void
  onSubmitInput:    () => void
  onToggleHint:     (type: 'letterCount' | 'firstLetter') => void
  onBack:           () => void
}) {
  const { t } = useTranslation()
  const wm      = t.wordmatch
  const elapsed = useTimer(state.startTime, state.endTime)
  const total   = state.questions.length
  const done    = state.answered
  const current = state.questions[state.currentIdx]
  const isHard  = difficulty === 'hard'

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && state.inputValue.trim()) onSubmitInput()
  }

  const counterText = wm.questionCounter
    .replace('{n}', String(done + 1))
    .replace('{total}', String(total))

  if (!current) return null

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={wm.title}
        stats={[
          { value: state.moves, label: wm.moves },
          { value: elapsed,     label: wm.time },
        ]}
        progress={(done / total) * 100}
        progressNow={done}
        progressMax={total}
        onBack={onBack}
        backLabel={wm.backToDashboard}
      />

      {/* Quiz area */}
      <main style={s.quizMain}>
        {/* Question counter pill */}
        <div style={s.counterPill}>{counterText}</div>

        {/* Clue card — shows image (images mode) or definition (definitions mode); hidden in anagram mode */}
        {mode !== 'anagram' && (
          <div style={s.defCard}>
            {mode === 'images' ? (
              current.imageUrl
                ? <img src={current.imageUrl} alt={current.word} style={s.defImageLarge} />
                : <span style={s.defEmoji}>{current.emoji}</span>
            ) : (
              <p style={s.defText}>{current.definition}</p>
            )}
          </div>
        )}

        {/* Scrambled letters — anagram mode only */}
        {mode === 'anagram' && (
          <div style={s.scrambleCard}>
            <span style={s.scrambleLabel}>{wm.anagramLabel}</span>
            <div style={s.scrambleLetters}>
              {state.scrambledLetters.split('').map((letter, i) => (
                <span key={i} style={s.scrambleLetter}>{letter.toUpperCase()}</span>
              ))}
            </div>
          </div>
        )}

        <p style={s.questionPrompt}>{wm.questionPrompt}</p>

        {/* Multiple-choice options (easy / medium) */}
        {!isHard && (
          <div style={s.optionsGrid}>
            {state.options.map((option) => {
              const isEliminated = state.eliminated.has(option.id)
              const isFlash      = state.correctFlashId === option.id
              return (
                <OptionButton
                  key={option.id}
                  word={option.word}
                  isEliminated={isEliminated}
                  isFlash={isFlash}
                  disabled={isEliminated || state.correctFlashId !== null}
                  onClick={() => onSelectOption(option.id)}
                />
              )
            })}
          </div>
        )}

        {/* Free-text input (hard) */}
        {isHard && (
          <div style={s.hardArea}>
            {/* Hint buttons */}
            <div style={s.hintRow}>
              {mode !== 'anagram' && (
                <button
                  onClick={() => onToggleHint('letterCount')}
                  style={{ ...s.hintBtn, ...(state.hintLetterCount ? s.hintBtnActive : {}) }}
                >
                  💡 {wm.hintLetterCount}
                </button>
              )}
              <button
                onClick={() => onToggleHint('firstLetter')}
                style={{ ...s.hintBtn, ...(state.hintFirstLetter ? s.hintBtnActive : {}) }}
              >
                💡 {wm.hintFirstLetter}
              </button>
            </div>

            {/* Hint results */}
            {mode !== 'anagram' && state.hintLetterCount && (
              <div style={s.hintResult}>
                {current.word.replace(/\s/g, '').length} {wm.letters}
              </div>
            )}
            {state.hintFirstLetter && (
              <div style={s.hintResult}>
                {wm.hintFirstLetter}: <strong>{current.word[0].toUpperCase()}</strong>
              </div>
            )}

            {/* Input row */}
            <div style={s.inputRow}>
              <input
                type="text"
                value={state.inputValue}
                onChange={(e) => onSetInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={wm.typeAnswer}
                disabled={state.correctFlashId !== null}
                style={{
                  ...s.textInput,
                  ...(state.inputStatus === 'wrong'   ? s.textInputWrong   : {}),
                  ...(state.inputStatus === 'correct' ? s.textInputCorrect : {}),
                }}
                autoComplete="off"
                autoCapitalize="off"
              />
              <button
                onClick={onSubmitInput}
                disabled={!state.inputValue.trim() || state.correctFlashId !== null}
                style={s.submitBtn}
              >
                {wm.submitBtn}
              </button>
            </div>

            {state.inputStatus === 'wrong' && (
              <p style={s.wrongFeedback}>{wm.wrongFeedback}</p>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

/* ── Option Button ───────────────────────────────────────────── */
function OptionButton({ word, isEliminated, isFlash, disabled, onClick }: {
  word:        string
  isEliminated: boolean
  isFlash:      boolean
  disabled:     boolean
  onClick:      () => void
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...s.optionBtn,
        ...(isEliminated ? s.optionBtnWrong   : {}),
        ...(isFlash      ? s.optionBtnCorrect : {}),
      }}
    >
      {isEliminated && <span style={s.optionX} aria-hidden>✗</span>}
      {isFlash      && <span style={s.optionCheck} aria-hidden>✓</span>}
      <span>{word}</span>
    </button>
  )
}
